// Section 4 phase 4, Sections 6 and 9: consumption. Computes each person's demand for food, water, warmth and medicine through the pipeline, and
// the needs that are conditions rather than stock: shelter, health care, safety and belonging.
// Food, water and warmth satisfaction are filled in by the rationing phase.
import { RATION_RESOURCES } from '../../core/constants';
import { effectiveCount, isChild, peopleSorted, safetyNeed } from '../../core/queries';
import type {
  NeedSatisfaction,
  Person,
  PersonId,
  Phase,
  PhaseContext,
  RationResource,
} from '../../core/types';
import { seal } from '../shared/seal';
import { num } from '../shared/value';

/**
 * D-25: belonging from an intact household, purposeful days, not grieving, a common hall.
 * Section 9: a common hall meets the belonging need for everyone (M6-07), so its strength (0..1,
 * condition-scaled, D-13) is a floor under the D-25 terms.
 */
function belonging(ctx: PhaseContext, p: Person, hall: number): number {
  const household = ctx.state.households[p.householdId];
  const intact = (household?.memberIds.length ?? 1) >= 2 ? 1 : 0;
  const purpose = p.job !== 'rest' || p.status.inSchool || isChild(ctx.cfg, p) ? 1 : 0;
  const grief = p.status.grieving === null ? 1 : 0;
  return Math.max(hall, (intact + purpose + grief + (hall > 0 ? 1 : 0)) / 4);
}

export const consumptionPhase: Phase = {
  id: 'consumption',
  reads: ['people', 'households', 'buildings', 'policies', 'currentReport'],
  writes: ['currentReport'],
  projection: 'run',
  run(ctx) {
    const report = ctx.report.current;
    const people = peopleSorted(ctx.state);
    const demandBy = {} as Record<RationResource, Record<PersonId, number>>;
    for (const r of RATION_RESOURCES) demandBy[r] = {};
    const needsBy: Record<PersonId, NeedSatisfaction> = {};
    // Section 6 and 13 safety need (stream M4a, core/queries.ts safetyNeed).
    const safety = safetyNeed(ctx.state, ctx.cfg);
    const hall = Math.min(1, effectiveCount(ctx.cfg, ctx.state, 'common-hall'));
    for (const p of people) {
      for (const r of RATION_RESOURCES) {
        demandBy[r][p.id] = Math.max(0, num(ctx, { kind: 'demand', personId: p.id, resource: r }));
      }
      const household = ctx.state.households[p.householdId];
      const housed = household !== undefined && household.buildingId !== null;
      const hurt = p.status.sick !== null || p.status.injured !== null;
      const needs: NeedSatisfaction = {
        food: 1,
        water: 1,
        warmth: 1,
        shelter: housed ? 1 : 0,
        healthCare: hurt ? 0 : 1,
        safety,
        belonging: belonging(ctx, p, hall),
      };
      needsBy[p.id] = needs;
    }
    report.demand = seal(ctx, demandBy);
    report.needs = seal(ctx, needsBy);
    return 'done';
  },
};
